import { useEffect, useRef } from 'react';
import { useQueryCache } from './QueryCacheProvider';

/**
 * Runs `onRefetch` whenever the query cache refetches `key` (a realtime refetch signal for that key, the app
 * regaining focus, coming back online, etc.) without fetching or caching anything under `key` itself.
 *
 * Use it to piggyback on another query's refetch signal, e.g. to delta-fetch related data when that query's
 * realtime channel reports a change.
 */
export const useRefetchSignal = (key: string | undefined, onRefetch: () => Promise<void>, { enabled = true }: { enabled?: boolean } = {}) => {
  const queryCache = useQueryCache();
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  useEffect(() => {
    if (!enabled || !key) return;
    return queryCache._registerRefetch(key, () => onRefetchRef.current());
  }, [queryCache, key, enabled]);
};
